import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import './PaymentMethodHistory.css';

const PaymentMethodHistory = () => {
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_BASE_URL = 'https://server.tradefxonline.com';

  useEffect(() => {
    fetchPaymentMethodHistory();
  }, []);

  const fetchPaymentMethodHistory = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/admin_payment_method_history.php`);
      
      if (response.data.status === 'success') {
        setHistoryData(response.data.data);
        setError(null);
      } else {
        setError('Failed to fetch payment method history');
        toast.error('Failed to fetch payment method history');
      }
    } catch (error) {
      console.error('Error fetching payment method history:', error);
      setError('Error fetching payment method history');
      toast.error('Error fetching payment method history');
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: '2-digit'
      }),
      time: date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    };
  };

  const getActionBadgeClass = (action) => {
    switch (action.toLowerCase()) {
      case 'added':
        return 'action-badge action-added';
      case 'updated':
        return 'action-badge action-updated';
      case 'deleted':
        return 'action-badge action-deleted';
      default:
        return 'action-badge action-default';
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'status-badge status-active';
      case 'inactive':
        return 'status-badge status-inactive';
      default:
        return 'status-badge status-default';
    }
  };

  if (loading) {
    return (
      <div className="payment-history-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading payment method history...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="payment-history-container">
        <div className="error-message">
          <h3>Error</h3>
          <p>{error}</p>
          <button onClick={fetchPaymentMethodHistory} className="retry-btn">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-method-history">
      <div className="payment-method-history-header">
        <h2>Payment Method History</h2>
        <button 
          className="refresh-btn" 
          onClick={fetchPaymentMethodHistory}
          disabled={loading}
        >
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {loading && (
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading payment method history...</p>
        </div>
      )}

      {error && (
        <div className="error-message">
          <p>Error: {error}</p>
          <button onClick={fetchPaymentMethodHistory}>Try Again</button>
        </div>
      )}

      {!loading && !error && historyData.length === 0 && (
        <div className="no-data">
          <p>No payment method history found.</p>
        </div>
      )}

      {!loading && !error && historyData.length > 0 && (
        <div className="table-container">
          <table className="payment-history-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Action</th>
                <th>Payment Type</th>
                <th>Wallet Name</th>
                <th>Wallet Address</th>
                <th>QR Code</th>
                <th>Status</th>
                <th>Date & Time</th>
              </tr>
            </thead>
            <tbody>
              {historyData.map((item) => (
                <tr key={item.id}>
                  <td>{item.id}</td>
                  <td>
                    <span className={`action-badge ${item.action.toLowerCase()}`}>
                      {item.action}
                    </span>
                  </td>
                  <td>{item.payment_type}</td>
                  <td>{item.wallet_name}</td>
                  <td className="wallet-address">{item.wallet_address}</td>
                  <td className="qr-cell">
                    {item.qr_upload && (
                      <img 
                        src={`https://server.tradefxonline.com/${item.qr_upload}`}
                        alt="QR Code"
                        className="qr-image"
                      />
                    )}
                  </td>
                  <td>
                    <span className={getStatusBadgeClass(item.status)}>
                      {item.status}
                    </span>
                  </td>
                  <td className="date-time">
                    <div className="date-time-wrapper">
                      <div className="date">{formatDateTime(item.action_date).date}</div>
                      <div className="time">{formatDateTime(item.action_date).time}</div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PaymentMethodHistory;